import React, { useState, useEffect, useRef } from 'react';
import { Header, Button, Modal, TextField } from '../layout';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const BASE_URL = API_URL.replace('/api', '');

const AdminDocumentsManagement = () => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'general',
        file: null
    });

    const [editFormData, setEditFormData] = useState({
        title: '',
        description: '',
        category: 'general'
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        setDocumentTitle('Gestione Documenti Generali');
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setIsLoading(true);
        try {
            const response = await api.getGeneralDocuments();
            if (response.status === 'success') {
                setDocuments(response.documents || []);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Impossibile caricare i documenti'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
            const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif'];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Tipo file non valido',
                    text: 'Sono ammessi solo file PDF, JPG, PNG e GIF'
                });
                e.target.value = '';
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File troppo grande',
                    text: 'La dimensione massima consentita è 10MB'
                });
                e.target.value = '';
                return;
            }

            setFormData(prev => ({
                ...prev,
                file: file,
                title: prev.title || file.name.replace(/\.[^/.]+$/, '')
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Il titolo è obbligatorio';
        if (!formData.file) newErrors.file = 'Seleziona un file';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddDocument = () => {
        setFormData({
            title: '',
            description: '',
            category: 'general',
            file: null
        });
        setErrors({});
        setModalOpen(true);
    };

    const handleEditDocument = (document) => {
        setCurrentDocument(document);
        setEditFormData({
            title: document.title,
            description: document.description || '',
            category: document.category || 'general'
        });
        setEditModalOpen(true);
    };

    const handleDeleteDocument = (document) => {
        Swal.fire({
            title: 'Sei sicuro?',
            text: `Vuoi eliminare il documento "${document.title}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Si, elimina',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.deleteGeneralDocument(document.id);
                    setDocuments(prev => prev.filter(d => d.id !== document.id));
                    Swal.fire({
                        icon: 'success',
                        title: 'Eliminato',
                        text: 'Documento eliminato con successo',
                        timer: 1500,
                        showConfirmButton: false
                    });
                } catch (error) {
                    console.error('Error deleting document:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Errore',
                        text: 'Impossibile eliminare il documento'
                    });
                }
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            Swal.fire({
                title: 'Caricamento in corso...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await api.uploadGeneralDocument(
                formData.file,
                formData.title,
                formData.description,
                formData.category
            );

            if (response.status === 'success') {
                setModalOpen(false);
                fetchDocuments();
                Swal.fire({
                    icon: 'success',
                    title: 'Caricato',
                    text: 'Documento caricato con successo',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: error.message || 'Impossibile caricare il documento'
            });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        try {
            await api.updateGeneralDocument(currentDocument.id, editFormData);
            setEditModalOpen(false);
            fetchDocuments();
            Swal.fire({
                icon: 'success',
                title: 'Aggiornato',
                text: 'Documento aggiornato con successo',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error updating document:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Impossibile aggiornare il documento'
            });
        }
    };

    const handlePreview = (document) => {
        const fileUrl = `${BASE_URL}${document.file_path}`;
        const isPdf = document.mime_type === 'application/pdf' || document.original_filename?.endsWith('.pdf');
        const isImage = document.mime_type?.startsWith('image/');

        if (isPdf || isImage) {
            Swal.fire({
                title: document.title,
                html: isPdf
                    ? `<iframe src="${fileUrl}" style="width:100%; height:70vh; border:none;"></iframe>`
                    : `<img src="${fileUrl}" style="max-width:100%; max-height:70vh;" alt="${document.title}" />`,
                width: '90%',
                showCloseButton: true,
                showConfirmButton: false,
            });
        } else {
            window.open(fileUrl, '_blank');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFileIcon = (mimeType, filename) => {
        const isPdf = mimeType === 'application/pdf' || filename?.endsWith('.pdf');
        if (isPdf) {
            return <span className="text-red-500 font-bold">PDF</span>;
        }
        return <span className="text-blue-500 font-bold">IMG</span>;
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <Header
                title="Gestione Documenti Generali"
                backText="Dashboard"
                backTo="/dashboard"
            />

            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">Documenti Generali</h2>
                    <Button
                        onClick={handleAddDocument}
                        color="primary"
                        className="flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Nuovo Documento
                    </Button>
                </div>

                <p className="text-gray-600 mb-6">
                    I documenti caricati qui saranno visibili a tutti gli utenti nella sezione "Consultazione Documenti".
                </p>

                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                    </div>
                ) : documents.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun documento</h3>
                        <p className="mt-1 text-sm text-gray-500">Inizia caricando il primo documento</p>
                        <div className="mt-6">
                            <Button onClick={handleAddDocument} color="primary">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Carica Documento
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titolo</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrizione</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dimensione</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {documents.map((doc) => (
                                        <tr key={doc.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getFileIcon(doc.mime_type, doc.original_filename)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-500 max-w-xs truncate">
                                                    {doc.description || '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{doc.original_filename}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{formatFileSize(doc.file_size)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{formatDate(doc.created_at)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <button
                                                    onClick={() => handlePreview(doc)}
                                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                                    title="Visualizza"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleEditDocument(doc)}
                                                    className="text-yellow-600 hover:text-yellow-900 mr-3"
                                                    title="Modifica"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteDocument(doc)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Elimina"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Carica Nuovo Documento"
                size="md"
            >
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Seleziona File *
                            </label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-red-400 transition-colors">
                                <div className="space-y-1 text-center">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 48 48"
                                    >
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className="flex text-sm text-gray-600">
                                        <label
                                            htmlFor="file-upload"
                                            className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none"
                                        >
                                            <span>Carica un file</span>
                                            <input
                                                id="file-upload"
                                                name="file-upload"
                                                type="file"
                                                className="sr-only"
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                accept=".pdf,.jpg,.jpeg,.png,.gif"
                                            />
                                        </label>
                                        <p className="pl-1">o trascina qui</p>
                                    </div>
                                    <p className="text-xs text-gray-500">PDF, PNG, JPG, GIF fino a 10MB</p>
                                </div>
                            </div>
                            {formData.file && (
                                <p className="mt-2 text-sm text-green-600">
                                    File selezionato: {formData.file.name}
                                </p>
                            )}
                            {errors.file && (
                                <p className="mt-1 text-sm text-red-600">{errors.file}</p>
                            )}
                        </div>

                        <TextField
                            id="title"
                            name="title"
                            label="Titolo"
                            value={formData.title}
                            onChange={handleInputChange}
                            error={errors.title}
                            required
                        />

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                Descrizione
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={formData.description}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder="Descrizione opzionale del documento..."
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setModalOpen(false)}
                        >
                            Annulla
                        </Button>
                        <Button type="submit" color="primary">
                            Carica Documento
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                title="Modifica Documento"
                size="md"
            >
                <form onSubmit={handleEditSubmit}>
                    <div className="space-y-4">
                        <TextField
                            id="edit-title"
                            name="title"
                            label="Titolo"
                            value={editFormData.title}
                            onChange={handleEditInputChange}
                            required
                        />

                        <div>
                            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
                                Descrizione
                            </label>
                            <textarea
                                id="edit-description"
                                name="description"
                                rows={3}
                                value={editFormData.description}
                                onChange={handleEditInputChange}
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setEditModalOpen(false)}
                        >
                            Annulla
                        </Button>
                        <Button type="submit" color="primary">
                            Salva Modifiche
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default AdminDocumentsManagement;
